import { useMemo, useState } from 'react';
import { usePayments } from './hooks/usePayments';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useMerchants } from '../merchants/hooks/useMerchants';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { RefundModal } from '../disputes/RefundModal';
import { formatMinor } from '../../shared/utils/money';
import type { ListPaymentsParams, Payment, PaymentStatus } from './types';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_FILTERS = ['', 'SUCCESS', 'PENDING', 'FAILED', 'REFUNDED', 'DISPUTED', 'EXPIRED'];
const PROVIDERS = ['', 'PAYSTACK', 'FLUTTERWAVE'];
const PAGE_SIZE = 20;

export default function Payments() {
  const { hasPermission } = usePermissions();
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [page, setPage] = useState(1);
  const [refundRef, setRefundRef] = useState<string | null>(null);

  const { data: merchantsData } = useMerchants();
  const merchantMap = useMemo(
    () => new Map<string, string>((merchantsData ?? []).map((m) => [m.id, m.name])),
    [merchantsData],
  );
  const merchantName = (tenantId: string) => merchantMap.get(tenantId) || tenantId;

  const params: ListPaymentsParams = {
    ...(status ? { status } : {}),
    ...(provider ? { provider } : {}),
    page,
    limit: PAGE_SIZE,
  };
  const { data, isLoading, error, isFetching } = usePayments(params);

  const canRefund = hasPermission(Permission.PAYMENT_REFUND);
  const payments: Payment[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetTo = (setter: (v: string) => void) => (value: string) => { setter(value); setPage(1); };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Payments</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
          <select
            className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            value={status}
            onChange={(e) => resetTo(setStatus)(e.target.value)}
          >
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s ? s.replace('_', ' ') : 'All statuses'}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Provider</label>
          <select
            className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            value={provider}
            onChange={(e) => resetTo(setProvider)(e.target.value)}
          >
            {PROVIDERS.map((p) => <option key={p} value={p}>{p || 'All providers'}</option>)}
          </select>
        </div>
      </div>

      {isLoading && <div className="text-center py-16 text-muted-foreground">Loading payments…</div>}
      {error && <div className="text-center py-16 text-red-500">Failed to load payments.</div>}
      {!isLoading && !error && payments.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">No payments found.</div>
      )}

      {!isLoading && !error && payments.length > 0 && (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Reference</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Provider</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Method</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Gross</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant Net</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</th>
                {canRefund && <th className="px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-muted transition-colors">
                  <td className="px-5 py-4 text-sm text-foreground font-mono max-w-[160px] truncate" title={p.reference}>{p.reference}</td>
                  <td className="px-5 py-4 text-sm text-muted-foreground max-w-[150px] truncate" title={p.tenantId}>{merchantName(p.tenantId)}</td>
                  <td className="px-5 py-4 text-sm text-muted-foreground">{p.provider}</td>
                  <td className="px-5 py-4 text-sm text-muted-foreground">{p.method.replace('_', ' ')}</td>
                  <td className="px-5 py-4 text-sm font-medium text-foreground">{formatMinor(p.grossAmount, p.currency)}</td>
                  <td className="px-5 py-4 text-sm text-foreground">{formatMinor(p.merchantNet, p.currency)}</td>
                  <td className="px-5 py-4"><PaymentStatusBadge status={p.status as PaymentStatus} /></td>
                  <td className="px-5 py-4 text-sm text-muted-foreground">{formatDate(p.createdAt)}</td>
                  {canRefund && (
                    <td className="px-5 py-4">
                      {p.status === 'SUCCESS' && (
                        <button
                          onClick={() => setRefundRef(p.reference)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {total} payment{total === 1 ? '' : 's'} · page {page} of {totalPages}{isFetching ? ' · …' : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((n) => Math.max(1, n - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {refundRef && <RefundModal reference={refundRef} onClose={() => setRefundRef(null)} />}
    </div>
  );
}
